import {INotificationDuplicateBarcodeError} from '../models/notification-duplicate-barcode-error.model';

const DUPLICATE_BARCODE_ERROR_CODE = '12';

/**
 * Parse IQ API XML error responses and extract stock items with duplicate barcode failures.
 */
export function parseDuplicateBarcodeErrorsFromXml(xmlResponse: string): INotificationDuplicateBarcodeError[] {
  if (!xmlResponse?.trim()) {
    return [];
  }

  try {
    const parser: DOMParser = new DOMParser();
    const doc: Document = parser.parseFromString(xmlResponse, 'text/xml');
    const stockNodes: HTMLCollectionOf<Element> = doc.getElementsByTagName('Stock');
    const results: INotificationDuplicateBarcodeError[] = [];

    Array.from(stockNodes).forEach((stockNode: Element) => {
      const errorNodes: HTMLCollectionOf<Element> = stockNode.getElementsByTagName('Error');
      const duplicateErrors: Element[] = Array.from(errorNodes).filter((errorNode: Element) => {
        const errorCode: string = errorNode.getElementsByTagName('Error_Code')[0]?.textContent?.trim() ?? '';
        const errorDescription: string = errorNode.getElementsByTagName('Error_Description')[0]?.textContent?.trim() ?? '';
        return errorCode === DUPLICATE_BARCODE_ERROR_CODE ||
          errorDescription.toLowerCase().includes('used with multiple barcodes') ||
          errorDescription.toLowerCase().includes('barcode');
      });

      if (duplicateErrors.length === 0) {
        return;
      }

      const stockCode: string = stockNode.getElementsByTagName('Stock_Code')[0]?.textContent?.trim() ?? '';
      if (!stockCode) {
        return;
      }

      const failedBarcodes: Set<string> = new Set<string>();
      const primaryBarcode: string = stockNode.getElementsByTagName('Barcode')[0]?.textContent?.trim() ?? '';
      if (primaryBarcode) {
        failedBarcodes.add(primaryBarcode);
      }

      const multipleBarcodeNodes: HTMLCollectionOf<Element> = stockNode.getElementsByTagName('Multiple_Barcodes');
      Array.from(multipleBarcodeNodes).forEach((multipleNode: Element) => {
        const barcodeNodes: HTMLCollectionOf<Element> = multipleNode.getElementsByTagName('Barcode');
        Array.from(barcodeNodes).forEach((barcodeNode: Element) => {
          const barcode: string = barcodeNode.textContent?.trim() ?? '';
          if (barcode) {
            failedBarcodes.add(barcode);
          }
        });
      });

      duplicateErrors.forEach((errorNode: Element) => {
        const errorDescription: string = errorNode.getElementsByTagName('Error_Description')[0]?.textContent?.trim() ?? '';
        const barcodeMatch: RegExpMatchArray | null = errorDescription.match(/^(\S+)\s+used with multiple barcodes/i);
        if (barcodeMatch?.[1]) {
          failedBarcodes.add(barcodeMatch[1]);
        }
      });

      if (failedBarcodes.size === 0) {
        return;
      }

      results.push({
        stockCode,
        failedBarcodes: Array.from(failedBarcodes),
        errorDescription: duplicateErrors[0]?.getElementsByTagName('Error_Description')[0]?.textContent?.trim(),
      });
    });

    return results;
  } catch {
    return [];
  }
}
